import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// --- Utilities ---
function euro(v) { return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
function pct(v, fallback = 'kein Vergleich') {
  if (v == null) return fallback;
  return `${v >= 0 ? '+' : ''}${Number(v).toFixed(1)} %`;
}
function fmtDatum(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  return `${d}.${m}.${y}`;
}
function fmtPeriode(s, gran) {
  if (!s) return s;
  if (gran === 'daily') return fmtDatum(s);
  const [y, m] = s.split('-');
  return `${m}/${y}`;
}

const COLORS = ['#2563eb','#16a34a','#f59e0b','#dc2626','#7c3aed','#0891b2','#db2777','#65a30d','#ea580c','#475569'];
const muted = { color: '#64748b', fontSize: '12px' };

// --- Period presets ---
const PRESETS = [
  { key: 'all',          label: 'Alle Daten' },
  { key: 'current_month',label: 'Aktueller Monat' },
  { key: 'last_month',   label: 'Letzter Monat' },
  { key: 'last_3m',      label: 'Letzte 3 Monate' },
  { key: 'last_6m',      label: 'Letzte 6 Monate' },
  { key: 'current_year', label: 'Aktuelles Jahr' },
  { key: 'last_year',    label: 'Letztes Jahr' },
  { key: 'custom',       label: 'Benutzerdefiniert' },
];

function presetToDates(key) {
  const t = new Date(); const y = t.getFullYear(); const m = t.getMonth();
  const iso = d => d.toISOString().slice(0, 10);
  switch (key) {
    case 'all':           return { von: null, bis: null };
    case 'current_month': return { von: `${y}-${String(m+1).padStart(2,'0')}-01`, bis: iso(t) };
    case 'last_month': {
      const von = new Date(y, m-1, 1); const bis = new Date(y, m, 0);
      return { von: iso(von), bis: iso(bis) };
    }
    case 'last_3m':  { const von = new Date(t); von.setMonth(von.getMonth()-3); return { von: iso(von), bis: iso(t) }; }
    case 'last_6m':  { const von = new Date(t); von.setMonth(von.getMonth()-6); return { von: iso(von), bis: iso(t) }; }
    case 'current_year': return { von: `${y}-01-01`, bis: iso(t) };
    case 'last_year':    return { von: `${y-1}-01-01`, bis: `${y-1}-12-31` };
    default:             return { von: null, bis: null };
  }
}

// --- KPI Card ---
function KpiCard({ label, value, sub, subColor, icon }) {
  return (
    <div className="an-card">
      <div style={{ ...muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', marginTop: '6px', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ ...muted, marginTop: '4px', color: subColor || '#64748b' }}>{sub}</div>}
    </div>
  );
}

// --- Anomaly item ---
function AnomalyItem({ item, onBuchung }) {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '8px' }}>
      <span style={{ fontSize: '18px' }}>⚠</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: '#92400e', fontSize: '13px' }}>{item.titel}</div>
        <div style={{ ...muted, color: '#78350f', marginTop: '2px' }}>{item.text}</div>
      </div>
      {item.buchungId && (
        <button onClick={() => onBuchung(item.buchungId)}
          style={{ background: 'none', border: '1px solid #f59e0b', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#92400e', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Buchung
        </button>
      )}
    </div>
  );
}

// --- Custom Tooltip for Composed Chart ---
function TrendTooltip({ active, payload, label, gran }) {
  if (!active || !payload?.length) return null;
  const ein = payload.find(p => p.dataKey === 'einnahmen')?.value || 0;
  const aus = payload.find(p => p.dataKey === 'ausgaben')?.value || 0;
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 700, marginBottom: '6px', color: '#0f172a' }}>{fmtPeriode(label, gran)}</div>
      <div style={{ color: '#16a34a' }}>Einnahmen: {euro(ein)}</div>
      <div style={{ color: '#dc2626' }}>Ausgaben: {euro(aus)}</div>
      <div style={{ color: (ein - aus) >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700, marginTop: '4px' }}>
        Saldo: {euro(ein - aus)}
      </div>
    </div>
  );
}

// =============================================
// MAIN COMPONENT
// =============================================
export default function DashboardPage() {
  const nav = useNavigate();
  const [preset, setPreset]   = useState('all');
  const [customVon, setCVon]  = useState('');
  const [customBis, setCBis]  = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [epic4, setEpic4]     = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/recurring/upcoming', { params: { days: 14 } }).catch(() => null),
      api.get('/fixkosten/summary').catch(() => null),
      api.get('/budgets/forecast').catch(() => null),
    ]).then(([upcoming, fixkosten, budgetForecast]) => {
      setEpic4({
        upcoming: upcoming?.data?.items || [],
        fixMonatlich: fixkosten?.data?.fixkosten?.monatlichAequivalent ?? null,
        budgetForecast: budgetForecast?.data?.items || []
      });
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (preset === 'custom') {
        if (customVon) params.von = customVon;
        if (customBis) params.bis = customBis;
      } else {
        const d = presetToDates(preset);
        if (d.von) params.von = d.von;
        if (d.bis) params.bis = d.bis;
      }
      const r = await api.get('/analytics/summary', { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Dashboard konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [preset, customVon, customBis]);

  useEffect(() => { load(); }, [load]);

  function navBuchungen(params) {
    const sp = new URLSearchParams(params);
    nav('/buchungen?' + sp.toString());
  }

  // derived
  const s = data?.summary || {};
  const v = data?.vergleich || {};
  const saldo = s.saldo || 0;
  const sonstig = data?.sonstigesInfo;
  const pieData = (data?.kategorienAusgaben || []).slice(0, 8);
  const trendsData = (data?.trends || []).map(t => ({
    ...t,
    einnahmen: t.einnahmen || 0,
    ausgaben: t.ausgaben || 0,
    saldo: t.saldo || 0,
  }));

  return (
    <div>
      {/* Header + Zeitraumfilter */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', fontSize: '22px' }}>Finanzanalyse</h1>
          {data && <p style={{ ...muted, margin: '3px 0 0' }}>
            {fmtDatum(data.zeitraum.von)} &ndash; {fmtDatum(data.zeitraum.bis)} &middot; {data.zeitraum.tage} Tage &middot; {s.buchungenAnzahl} Buchungen
          </p>}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={preset} onChange={e => setPreset(e.target.value)}
            style={{ padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', fontSize: '13px' }}>
            {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          {preset === 'custom' && (
            <>
              <input type="date" value={customVon} onChange={e => setCVon(e.target.value)}
                style={{ padding: '7px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }} aria-label="Von-Datum" />
              <span style={muted}>bis</span>
              <input type="date" value={customBis} onChange={e => setCBis(e.target.value)}
                style={{ padding: '7px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }} aria-label="Bis-Datum" />
            </>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', fontWeight: 700 }}>{error}</div>}
      {loading && <div className="an-card">Analyse wird geladen...</div>}

      {!loading && data && (
        <>
          {/* Sonstiges-Qualitaetsindikator */}
          {sonstig?.hinweis && (
            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px' }}>
              <span style={{ color: '#92400e', fontWeight: 700 }}>Datenqualitaet: </span>
              <span style={{ color: '#78350f' }}>{sonstig.hinweis}</span>
              <button onClick={() => navBuchungen({ kategorie: 'Sonstiges', von: data.zeitraum.von, bis: data.zeitraum.bis })}
                style={{ marginLeft: '12px', background: 'none', border: '1px solid #f59e0b', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', color: '#92400e', cursor: 'pointer' }}>
                Anzeigen
              </button>
            </div>
          )}

          {/* KPI-Zeile */}
          <div className="an-kpi-grid">
            <KpiCard label="Einnahmen" icon="&#x2b06;" value={euro(s.einnahmen)}
              sub={v.vorhanden ? `Vorperiode: ${euro(v.einnahmen)}` : 'Kein Vergleich'} />
            <KpiCard label="Ausgaben" icon="&#x2b07;" value={euro(s.ausgaben)}
              sub={v.vorhanden ? `Vorperiode: ${euro(v.ausgaben)}` : 'Kein Vergleich'}
              subColor={v.vorhanden && v.ausgaben != null && s.ausgaben > v.ausgaben ? '#dc2626' : '#64748b'} />
            <KpiCard label={saldo >= 0 ? 'Überschuss' : 'Defizit'} icon={saldo >= 0 ? '✔' : '⚠'}
              value={euro(Math.abs(saldo))}
              sub={v.vorhanden ? `Vorperiode: ${euro(v.saldo)}` : 'Kein Vergleich'}
              subColor={saldo >= 0 ? '#16a34a' : '#dc2626'} />
            <KpiCard label="Sparquote" icon="\u{1F4B0}"
              value={s.sparquote != null ? pct(s.sparquote, '') : 'n/a'}
              sub={s.sparquote != null ? 'Saldo / Einnahmen' : 'Einnahmen = 0'}
              subColor={s.sparquote != null && s.sparquote >= 0 ? '#16a34a' : '#dc2626'} />
          </div>

          {/* Trend-Chart */}
          {trendsData.length > 0 && (
            <div className="an-card" style={{ marginBottom: '12px' }}>
              <h2 style={{ margin: '0 0 12px', color: '#0f172a', fontSize: '16px' }}>
                Einnahmen und Ausgaben &mdash; {data.granularity === 'daily' ? 'Tagesverlauf' : 'Monatsverlauf'}
              </h2>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={trendsData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="periode" tick={{ fontSize: 11 }} tickFormatter={s => fmtPeriode(s, data.granularity)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip content={<TrendTooltip gran={data.granularity} />} />
                  <Bar dataKey="einnahmen" name="Einnahmen" fill="#16a34a" radius={[4,4,0,0]} />
                  <Bar dataKey="ausgaben"  name="Ausgaben"  fill="#dc2626" radius={[4,4,0,0]} />
                  <Line type="monotone" dataKey="saldo" name="Saldo" stroke="#2563eb" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          {trendsData.length === 0 && (
            <div className="an-card" style={{ marginBottom: '12px', color: '#64748b' }}>
              Keine Buchungen im gewaelten Zeitraum.
            </div>
          )}

          {/* Kategorien + Top-Ausgaben */}
          <div className="an-2col">
            {/* Kategorie-Tabelle mit Pie */}
            <div className="an-card">
              <h2 style={{ margin: '0 0 12px', color: '#0f172a', fontSize: '16px' }}>Ausgaben nach Kategorie</h2>
              {pieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} dataKey="betrag" nameKey="kategorie"
                        cx="50%" cy="50%" outerRadius={75}
                        onClick={k => navBuchungen({ kategorie: k.kategorie, von: data.zeitraum.von, bis: data.zeitraum.bis })}>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} style={{ cursor: 'pointer' }} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [euro(v), n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="an-table">
                      <thead>
                        <tr>
                          <th>Kategorie</th>
                          <th style={{ textAlign: 'right' }}>Betrag</th>
                          <th style={{ textAlign: 'right' }}>Anteil</th>
                          <th style={{ textAlign: 'right' }}>Buchungen</th>
                          {v.vorhanden && <th style={{ textAlign: 'right' }}>Aend.</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(data.kategorienAusgaben || []).map((k, i) => (
                          <tr key={k.kategorie}
                            onClick={() => navBuchungen({ kategorie: k.kategorie, von: data.zeitraum.von, bis: data.zeitraum.bis })}
                            title={`${k.kategorie} anzeigen`}>
                            <td>
                              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], marginRight: 6 }} />
                              {k.icon} {k.kategorie}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{euro(k.betrag)}</td>
                            <td style={{ textAlign: 'right', color: '#64748b' }}>{k.anteil} %</td>
                            <td style={{ textAlign: 'right', color: '#64748b' }}>{k.anzahl}</td>
                            {v.vorhanden && (
                              <td style={{ textAlign: 'right', color: k.veraenderung == null ? '#94a3b8' : k.veraenderung > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                                {pct(k.veraenderung, '—')}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : <div style={muted}>Keine Ausgaben im Zeitraum.</div>}
            </div>

            {/* Top 10 Ausgaben */}
            <div className="an-card">
              <h2 style={{ margin: '0 0 12px', color: '#0f172a', fontSize: '16px' }}>Groesste Ausgaben</h2>
              {(data.topAusgaben || []).length === 0
                ? <div style={muted}>Keine Ausgaben im Zeitraum.</div>
                : (data.topAusgaben || []).map((b, i) => (
                    <div key={b.id}
                      onClick={() => nav('/buchungen')}
                      style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '8px 0', borderBottom: i < data.topAusgaben.length - 1 ? '1px solid #f1f5f9' : 'none', cursor: 'pointer' }}
                      title="Buchungen öffnen">
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {b.empfaenger || 'Unbekannt'}
                        </div>
                        <div style={muted}>{fmtDatum(b.datum)} &middot; {b.kategorie}</div>
                      </div>
                      <div style={{ color: '#dc2626', fontWeight: 900, whiteSpace: 'nowrap', alignSelf: 'center' }}>
                        {euro(Math.abs(b.betrag))}
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>

          {/* Haeufigste Empfaenger */}
          {(data.topEmpfaenger || []).length > 0 && (
            <div className="an-card" style={{ marginBottom: '12px' }}>
              <h2 style={{ margin: '0 0 10px', color: '#0f172a', fontSize: '16px' }}>Häufigste Empfänger</h2>
              <div style={{ overflowX: 'auto' }}>
                <table className="an-table">
                  <thead><tr>
                    <th>Empfänger</th><th>Kategorie</th>
                    <th style={{ textAlign: 'right' }}>Buchungen</th>
                    <th style={{ textAlign: 'right' }}>Summe</th>
                  </tr></thead>
                  <tbody>
                    {data.topEmpfaenger.map(e => (
                      <tr key={e.empfaenger}
                        onClick={() => navBuchungen({ q: e.empfaenger, von: data.zeitraum.von, bis: data.zeitraum.bis })}
                        title="Buchungen dieses Empfängers anzeigen">
                        <td style={{ fontWeight: 600, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.empfaenger}</td>
                        <td style={{ color: '#64748b' }}>{e.kategorie}</td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>{e.anzahl}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{euro(e.summe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Auffaelligkeiten */}
          {(data.auffaelligkeiten || []).length > 0 && (
            <div className="an-card" style={{ marginBottom: '12px' }}>
              <h2 style={{ margin: '0 0 10px', color: '#0f172a', fontSize: '16px' }}>Auffälligkeiten</h2>
              {data.auffaelligkeiten.map((item, i) => (
                <AnomalyItem key={i} item={item} onBuchung={() => nav('/buchungen')} />
              ))}
            </div>
          )}

          {/* EPIC 4: Kompakte Planung-Widgets */}
          {epic4 && (epic4.fixMonatlich != null || epic4.upcoming?.length > 0 || epic4.budgetForecast?.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              {epic4.fixMonatlich != null && (
                <div className="an-card" style={{ cursor: 'pointer' }} onClick={() => nav('/prognose')}>
                  <div style={{ ...muted, fontWeight: 700, textTransform: 'uppercase', fontSize: 11 }}>Fixkosten/Monat</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444', marginTop: 4 }}>{euro(epic4.fixMonatlich)}</div>
                  <div style={{ ...muted, marginTop: 2, fontSize: 11 }}>Bestätigte Fixkosten → Prognose</div>
                </div>
              )}
              {epic4.upcoming?.length > 0 && (
                <div className="an-card" style={{ cursor: 'pointer' }} onClick={() => nav('/wiederkehrend')}>
                  <div style={{ ...muted, fontWeight: 700, textTransform: 'uppercase', fontSize: 11 }}>Demnächst (14 Tage)</div>
                  {epic4.upcoming.slice(0, 3).map((u, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 12 }}>
                      <span style={{ color: '#64748b' }}>{u.datum} {u.bezeichnung?.slice(0, 22)}</span>
                      <span style={{ color: (u.betrag || 0) < 0 ? '#ef4444' : '#10b981', fontWeight: 700 }}>
                        {u.isVariable ? 'ca. ' : ''}{euro(u.betrag)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Vergleichsperiode-Info */}
          <div style={{ ...muted, textAlign: 'center', paddingTop: '4px', paddingBottom: '60px' }}>
            {v.vorhanden
              ? `Vergleichsperiode: ${fmtDatum(v.von)} – ${fmtDatum(v.bis)}`
              : 'Keine Vergleichsdaten für diesen Zeitraum vorhanden.'}
          </div>
        </>
      )}
    </div>
  );
}
